import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FileText, Plus, Trash2, Lock, Unlock, CalendarRange, AlertTriangle, Wand2, GraduationCap } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const DEFAULT_TERM_NAMES = ['Trimestre 1', 'Trimestre 2', 'Trimestre 3'];
const emptyForm = { name: '', term_id: '', academic_year_id: '', start_date: '', end_date: '', class_ids: [] };

export default function ExamsPage() {
  const { t } = useT();
  const orgId = useOrgId();
  const { selectedYear, selectedYearId } = useAcademicYear();
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [years, setYears] = useState([]);
  const [terms, setTerms] = useState([]); // terms of the form's selected year
  const [loading, setLoading] = useState(true);

  // page filters
  const [filterYearId, setFilterYearId] = useState('');
  const [filterTermId, setFilterTermId] = useState('');
  const [filterTerms, setFilterTerms] = useState([]);
  const [filterClassId, setFilterClassId] = useState('');

  // create dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [creatingTerms, setCreatingTerms] = useState(false);

  // bulk "create 3 trimester exams" helper
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkYearId, setBulkYearId] = useState('');
  const [bulkClassIds, setBulkClassIds] = useState([]);
  const [bulkCreating, setBulkCreating] = useState(false);

  const activeYear = selectedYear || years.find((y) => y.is_active) || years[0];

  // Map class id -> name, and resolve an exam's class names
  const classNameById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.name])), [classes]);
  const examClassNames = useCallback(
    (ex) => (ex.class_ids || []).map((id) => classNameById[id]).filter(Boolean),
    [classNameById],
  );
  // Client-side class filter (exam list is small; consistent with year/term filters)
  const visibleExams = useMemo(
    () => (filterClassId ? exams.filter((ex) => (ex.class_ids || []).includes(filterClassId)) : exams),
    [exams, filterClassId],
  );

  const loadExams = useCallback(async () => {
    if (!orgId) return;
    const params = {};
    if (filterYearId) params.academic_year_id = filterYearId;
    if (filterTermId) params.term_id = filterTermId;
    setExams(await schoolApi.listExams(orgId, params));
  }, [orgId, filterYearId, filterTermId]);

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      setLoading(true);
      const [cls, yrs] = await Promise.all([schoolApi.listClasses(orgId, selectedYearId), schoolApi.listYears(orgId)]);
      setClasses(cls); setYears(yrs);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [orgId, selectedYearId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadExams(); }, [loadExams]);

  // Follow the global academic-year selector: the exam list + term filter track it.
  useEffect(() => { setFilterYearId(selectedYearId || ''); setFilterTermId(''); }, [selectedYearId]);

  // load terms for the page filter when the filter year changes
  useEffect(() => {
    if (!orgId || !filterYearId) { setFilterTerms([]); setFilterTermId(''); return; }
    schoolApi.listTerms(orgId, filterYearId).then(setFilterTerms);
  }, [orgId, filterYearId]);

  // load terms for the create dialog when its year changes
  const loadFormTerms = useCallback(async (yearId) => {
    if (!orgId || !yearId) { setTerms([]); return; }
    setTerms(await schoolApi.listTerms(orgId, yearId));
  }, [orgId]);

  const openCreate = async () => {
    const yId = activeYear?.id || '';
    setForm({ ...emptyForm, academic_year_id: yId });
    await loadFormTerms(yId);
    setDialogOpen(true);
  };

  const onFormYearChange = async (yId) => {
    setForm((f) => ({ ...f, academic_year_id: yId, term_id: '' }));
    await loadFormTerms(yId);
  };

  const createDefaultTerms = async (yearId) => {
    setCreatingTerms(true);
    try {
      const existing = await schoolApi.listTerms(orgId, yearId);
      const haveSeq = new Set(existing.map((tm) => tm.sequence));
      for (let i = 0; i < 3; i++) {
        if (!haveSeq.has(i + 1)) {
          await schoolApi.createTerm({ organization_id: orgId, academic_year_id: yearId, name: DEFAULT_TERM_NAMES[i], sequence: i + 1 });
        }
      }
      const refreshed = await schoolApi.listTerms(orgId, yearId);
      setTerms(refreshed);
      toast.success(t('exams.termsCreated', 'Trimesters created'));
      return refreshed;
    } catch (e) { toast.error('Failed to create terms'); return []; }
    finally { setCreatingTerms(false); }
  };

  const toggleClass = (id) => setForm((f) => ({ ...f, class_ids: f.class_ids.includes(id) ? f.class_ids.filter((c) => c !== id) : [...f.class_ids, id] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('common.required')); return; }
    if (!form.academic_year_id) { toast.error(t('exams.yearRequired', 'Academic year is required')); return; }
    if (!form.term_id) { toast.error(t('exams.termRequired', 'Trimester is required for bulletins')); return; }
    setSaving(true);
    try {
      await schoolApi.createExam({
        organization_id: orgId, name: form.name, term_id: form.term_id,
        academic_year_id: form.academic_year_id, class_ids: form.class_ids,
        start_date: form.start_date || null, end_date: form.end_date || null,
      });
      toast.success(t('common.saved')); setDialogOpen(false); setForm(emptyForm); loadExams();
    } catch (e) { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const toggleLock = async (ex) => { try { await schoolApi.updateExam(ex.id, { is_locked: !ex.is_locked }); toast.success(ex.is_locked ? 'Unlocked' : 'Locked'); loadExams(); } catch (e) { toast.error('Failed'); } };
  const del = async (id) => { try { await schoolApi.deleteExam(id); toast.success(t('common.deleted')); loadExams(); } catch (e) { toast.error('Failed'); } };

  // ---- bulk helper ----
  const openBulk = () => { setBulkYearId(activeYear?.id || ''); setBulkClassIds([]); setBulkOpen(true); };
  const toggleBulkClass = (id) => setBulkClassIds((p) => (p.includes(id) ? p.filter((c) => c !== id) : [...p, id]));

  const runBulkCreate = async () => {
    if (!bulkYearId) { toast.error(t('exams.yearRequired', 'Academic year is required')); return; }
    setBulkCreating(true);
    try {
      let yearTerms = await schoolApi.listTerms(orgId, bulkYearId);
      if (yearTerms.length < 3) {
        const haveSeq = new Set(yearTerms.map((tm) => tm.sequence));
        for (let i = 0; i < 3; i++) {
          if (!haveSeq.has(i + 1)) {
            await schoolApi.createTerm({ organization_id: orgId, academic_year_id: bulkYearId, name: DEFAULT_TERM_NAMES[i], sequence: i + 1 });
          }
        }
        yearTerms = await schoolApi.listTerms(orgId, bulkYearId);
      }
      const sorted = [...yearTerms].sort((a, b) => (a.sequence || 0) - (b.sequence || 0)).slice(0, 3);
      for (const tm of sorted) {
        await schoolApi.createExam({
          organization_id: orgId, name: `Bulletin — ${tm.name}`, term_id: tm.id,
          academic_year_id: bulkYearId, class_ids: bulkClassIds,
        });
      }
      toast.success(t('exams.bulkDone', `Created ${sorted.length} trimester exams`));
      setBulkOpen(false); loadExams();
    } catch (e) { toast.error('Failed to create trimester exams'); }
    finally { setBulkCreating(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('nav.exams')} subtitle={t('exams.subtitle', 'Create one exam per trimester — bulletins are built per trimester')} icon={FileText}
        actions={(
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={openBulk} className="gap-2" data-testid="bulk-exams-button"><Wand2 className="w-4 h-4" /> {t('exams.createTrimesters', 'Create Trimester Exams')}</Button>
            <Button onClick={openCreate} className="gap-2" data-testid="add-exam-button"><Plus className="w-4 h-4" /> {t('common.add')}</Button>
          </div>
        )} />

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Select value={filterTermId || 'all'} onValueChange={(v) => setFilterTermId(v === 'all' ? '' : v)} disabled={!filterYearId}>
            <SelectTrigger data-testid="exams-term-filter"><SelectValue placeholder={t('years.terms', 'Trimester')} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t('common.all', 'All')} {t('years.terms', 'Terms')}</SelectItem>{filterTerms.map((tm) => <SelectItem key={tm.id} value={tm.id}>{tm.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterClassId || 'all'} onValueChange={(v) => setFilterClassId(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="exams-class-filter"><SelectValue placeholder={t('students.class', 'Class')} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t('common.all', 'All')} {t('dash.classes', 'Classes')}</SelectItem>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? <LoadingState rows={4} /> : visibleExams.length === 0 ? (
        <Card><EmptyState icon={FileText} title={t('common.noData')} description={t('exams.empty', 'Create your first trimester exam')} actionLabel={t('common.add')} onAction={openCreate} /></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleExams.map((ex) => (
            <Card key={ex.id} className="p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{ex.name}</p>
                  <p className="text-xs text-muted-foreground">{ex.academic_year_name || '—'} &middot; {ex.class_ids?.length || 0} {t('dash.classes')}</p>
                </div>
                <Badge variant="outline" className={ex.is_locked ? 'text-amber-600 border-amber-200 bg-amber-50' : 'text-emerald-600 border-emerald-200 bg-emerald-50'}>
                  {ex.is_locked ? <Lock className="w-3 h-3 mr-1" /> : <Unlock className="w-3 h-3 mr-1" />}{ex.is_locked ? 'Locked' : 'Open'}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {ex.term_name ? (
                  <Badge variant="outline" className="gap-1 text-[color:var(--eli-crest-navy,#1e3a5f)]"><CalendarRange className="w-3 h-3" /> {ex.term_name}</Badge>
                ) : (
                  <Badge variant="outline" className="gap-1 text-amber-700 border-amber-200 bg-amber-50" data-testid={`exam-noterm-${ex.id}`}><AlertTriangle className="w-3 h-3" /> {t('exams.noTerm', 'No trimester — not on bulletins')}</Badge>
                )}
                {ex.term_name && <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">{t('exams.bulletinReady', 'Bulletin-ready')}</Badge>}
              </div>
              {/* Class names covered by this bulletin */}
              <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid={`exam-classes-${ex.id}`}>
                <GraduationCap className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                {examClassNames(ex).length > 0 ? (
                  examClassNames(ex).map((nm) => (
                    <Badge key={nm} variant="secondary" className="font-normal">{nm}</Badge>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">{t('exams.noClasses', 'No classes assigned')}</span>
                )}
              </div>
              {(ex.start_date || ex.end_date) && (
                <p className="text-xs text-muted-foreground mt-2">{ex.start_date || '?'} &rarr; {ex.end_date || '?'}</p>
              )}
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => toggleLock(ex)} data-testid={`toggle-lock-${ex.id}`}>{ex.is_locked ? <Unlock className="w-3.5 h-3.5 mr-1" /> : <Lock className="w-3.5 h-3.5 mr-1" />}{ex.is_locked ? 'Unlock' : 'Lock'}</Button>
                <Button size="icon" variant="ghost" className="text-red-600" onClick={() => del(ex.id)} data-testid={`delete-exam-${ex.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create exam dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="exam-dialog">
          <DialogHeader>
            <DialogTitle>{t('common.add')} {t('nav.exams')}</DialogTitle>
            <DialogDescription>{t('exams.dialogHint', 'Each trimester = one exam. Bulletins are generated per trimester.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>{t('common.name')} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bulletin — Trimestre 1" data-testid="exam-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('years.title', 'Academic Year')} *</Label>
                <Select value={form.academic_year_id} onValueChange={onFormYearChange}>
                  <SelectTrigger data-testid="exam-year-select"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('years.terms', 'Trimester')} *</Label>
                <Select value={form.term_id || ''} onValueChange={(v) => setForm({ ...form, term_id: v })} disabled={!form.academic_year_id || terms.length === 0}>
                  <SelectTrigger data-testid="exam-term-select"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>{terms.map((tm) => <SelectItem key={tm.id} value={tm.id}>{tm.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {form.academic_year_id && terms.length === 0 && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="exam-no-terms-notice">
                <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> {t('exams.noTermsYet', 'No trimesters defined for this year.')}</span>
                <Button size="sm" variant="outline" disabled={creatingTerms} onClick={() => createDefaultTerms(form.academic_year_id)} data-testid="create-default-terms-button">
                  {creatingTerms ? t('common.saving') : t('exams.createTermsBtn', 'Create Trimestre 1/2/3')}
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('years.startDate')}</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>{t('years.endDate')}</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div>
              <Label>{t('nav.classes')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1 border border-border rounded p-3 max-h-40 overflow-y-auto">
                {classes.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.class_ids.includes(c.id)} onCheckedChange={() => toggleClass(c.id)} data-testid={`exam-class-${c.id}`} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button><Button onClick={save} disabled={saving} data-testid="exam-save-button">{saving ? t('common.saving') : t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk: create 3 trimester exams */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-exam-dialog">
          <DialogHeader>
            <DialogTitle>{t('exams.createTrimesters', 'Create Trimester Exams')}</DialogTitle>
            <DialogDescription>{t('exams.bulkHint', 'Creates one exam for each trimester (1/2/3) of the selected year, linked to the selected classes. Missing trimesters are created automatically.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('years.title', 'Academic Year')} *</Label>
              <Select value={bulkYearId} onValueChange={setBulkYearId}>
                <SelectTrigger data-testid="bulk-year-select"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('nav.classes')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1 border border-border rounded p-3 max-h-48 overflow-y-auto">
                {classes.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={bulkClassIds.includes(c.id)} onCheckedChange={() => toggleBulkClass(c.id)} data-testid={`bulk-class-${c.id}`} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkCreating}>{t('common.cancel')}</Button>
            <Button onClick={runBulkCreate} disabled={bulkCreating || !bulkYearId} data-testid="bulk-create-button">{bulkCreating ? t('common.saving') : t('exams.createTrimesters', 'Create Trimester Exams')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
